/**
 * Gap Analysis report draft (workflow slide 8, "review and approve the report, present, THEN push").
 *
 * The draft is built ONLY from real project data (company profile attributes, validated scores). A
 * section the data cannot supply stays EMPTY and is shown as "Not provided"; nothing is invented.
 * Before the draft can be saved (which unlocks Send for Review / Push) the Advisor must, for every
 * section that was not derived from project data, either confirm the text they wrote or mark the
 * section not applicable. Absent beats fake.
 */

export const NOT_PROVIDED_TEXT = 'Not provided: add from the company profile or write it';
export const NOT_APPLICABLE_TEXT = 'Not applicable (marked by the Advisor)';

/** Sections the Advisor must confirm (or mark not applicable) unless they were derived from project data. */
export const REPORT_CONFIRMABLE_FIELDS = [
  { key: 'legalEntities', label: 'Legal entities and locations' },
  { key: 'productsServices', label: 'Products and services' },
  { key: 'activitiesDepts', label: 'Activities / departments' },
  { key: 'affirmationName', label: 'Affirmation name' },
  { key: 'affirmationDate', label: 'Affirmation date' },
];

const attr = (project, key) => {
  const attrs = Array.isArray(project?.profile?.attributes) ? project.profile.attributes : [];
  const hit = attrs.find((a) => a && a.key === key);
  return hit && typeof hit.value === 'string' ? hit.value.trim() : '';
};

/** The real company name for the report: legal name from the company profile, else the project's company. Never a fallback name. */
export function reportCompanyName(project) {
  return attr(project, 'legalName') || (typeof project?.companyName === 'string' ? project.companyName.trim() : '');
}

function profileLocations(project) {
  const places = [];
  const hq = attr(project, 'hqCity');
  if (hq) places.push(hq);
  const raw = attr(project, 'locationDetails');
  if (raw) {
    try {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) {
        arr.forEach((loc) => {
          const city = String(loc?.city || loc?.Location || '').trim();
          const country = String(loc?.country || '').trim();
          const place = [city, country].filter(Boolean).join(', ');
          if (place && !places.includes(place)) places.push(place);
        });
      }
    } catch {
      /* unreadable location JSON: contributes nothing rather than guessing */
    }
  }
  return places;
}

/**
 * @param {object} args
 * @param {object} args.project
 * @param {{domain:string,total:number,fulfilling:number}[]} args.domainCounts  from validated scores
 * @param {number} args.overallScore
 * @returns {{ ok: false, reason: string } | { ok: true, sections: object }}
 */
export function buildReportDraft({ project, domainCounts, overallScore }) {
  const company = reportCompanyName(project);
  if (!company) {
    return { ok: false, reason: 'This project has no company name. Add it to the company profile before generating the report.' };
  }
  const rows = Array.isArray(domainCounts) ? domainCounts.filter((d) => d && d.total > 0) : [];
  if (rows.length === 0) {
    return { ok: false, reason: 'There are no scored controls to report on yet. Validate the scores first.' };
  }
  const totalControls = rows.reduce((s, d) => s + d.total, 0);
  const totalFulfilling = rows.reduce((s, d) => s + d.fulfilling, 0);
  const standard = typeof project?.certification === 'string' && project.certification.trim() ? ` against ${project.certification.trim()}` : '';
  const weakest = rows
    .slice()
    .sort((a, b) => a.fulfilling / a.total - b.fulfilling / b.total || a.domain.localeCompare(b.domain))
    .slice(0, 3)
    .filter((d) => d.fulfilling < d.total);
  const parts = [
    `This report presents the findings of the Gap Analysis conducted for ${company}${standard}.`,
    `The assessment covered ${totalControls} controls across ${rows.length} domains. The overall readiness score is ${overallScore}%.`,
    `${totalFulfilling} of ${totalControls} assessed controls meet the expected maturity threshold.`,
  ];
  if (weakest.length) {
    parts.push(`Domains with the fewest controls meeting the threshold: ${weakest.map((d) => `${d.domain} (${d.fulfilling} of ${d.total})`).join(', ')}.`);
  }

  const origin = { executiveSummary: 'derived' };
  const sections = {
    executiveSummary: parts.join(' '),
    legalEntities: '',
    productsServices: '',
    activitiesDepts: '',
    affirmationName: '',
    affirmationDate: '',
  };
  const places = profileLocations(project);
  if (places.length) {
    sections.legalEntities = `${company} - ${places.join(', ')}`;
    origin.legalEntities = 'derived';
  }
  const products = attr(project, 'productsAndServices') || attr(project, 'apps');
  if (products) {
    sections.productsServices = products;
    origin.productsServices = 'derived';
  }
  return { ok: true, sections: { ...sections, sectionOrigin: origin, sectionStatus: {} } };
}

/** Labels of the sections the Advisor still has to confirm, fill in, or mark not applicable. */
export function unconfirmedReportSections(sections) {
  const s = sections || {};
  const status = s.sectionStatus || {};
  const origin = s.sectionOrigin || {};
  return REPORT_CONFIRMABLE_FIELDS.filter(({ key }) => {
    if (status[key] === 'na') return false;
    const text = typeof s[key] === 'string' ? s[key].trim() : '';
    if (!text) return true;
    if (origin[key] === 'derived') return false;
    return status[key] !== 'confirmed';
  }).map((f) => f.label);
}

/** Edit a section's text: it is then the Advisor's own wording, so any earlier confirmation lapses. */
export function withReportField(sections, key, value) {
  return {
    ...sections,
    [key]: value,
    sectionOrigin: { ...(sections.sectionOrigin || {}), [key]: 'manual' },
    sectionStatus: Object.fromEntries(Object.entries(sections.sectionStatus || {}).filter(([k]) => k !== key)),
  };
}

export function withReportFieldStatus(sections, key, status) {
  const next = Object.fromEntries(Object.entries(sections.sectionStatus || {}).filter(([k]) => k !== key));
  if (status) next[key] = status;
  return { ...sections, ...(status === 'na' ? { [key]: '' } : {}), sectionStatus: next };
}

// Literal text an earlier build wrote into every generated draft. It was invented, never project data.
const LEGACY_CANNED_FRAGMENTS = [
  'Key areas of concern include access management, incident response capabilities, and vendor risk governance',
  'Mumbai HQ, Bangalore Tech Center, Pune Dev Hub',
  'Cloud SaaS Platform, Mobile Application Suite, API Gateway Services',
  'Engineering, Product, IT Operations, HR, Finance, Legal, Customer Support',
  'PolicyLense Advisory LLP',
  'Nexus Corp',
];

export function hasLegacyCannedReportText(draft) {
  if (!draft || typeof draft !== 'object') return false;
  return Object.values(draft).some((v) => typeof v === 'string' && LEGACY_CANNED_FRAGMENTS.some((f) => v.includes(f)));
}

/** A saved draft carrying invented text is emptied (not edited) so the Advisor regenerates it from real data. */
export function withoutLegacyCannedText(draft) {
  if (!hasLegacyCannedReportText(draft)) return draft;
  const blank = { executiveSummary: '', legalEntities: '', productsServices: '', activitiesDepts: '', affirmationName: '', affirmationDate: '' };
  return blank;
}
